import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaBars, FaTimes, FaUser, FaSignOutAlt, FaKey, FaChevronDown } from 'react-icons/fa';
import { setUserPasswordApi } from '../api/http';

export default function Navbar() {
  const { user, logout, hasRole, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const userMenuRef = useRef(null);
  const mobileUserMenuRef = useRef(null);

  const items = [
    { to: '/', label: 'Inicio', roles: ['admin', 'programmer'] },
    { to: '/users', label: 'Programadores', roles: ['admin'] },
    { to: '/approvals', label: 'Aprobaciones', roles: ['admin'] },
    { to: '/clients', label: 'Clientes', roles: ['admin'] },
    { to: '/projects', label: 'Proyectos', roles: ['admin', 'programmer'] },
    { to: '/bitacora', label: 'Bitácora', roles: ['admin', 'programmer'] },
  ];

  const can = (roles = []) => {
    if (hasRole) return roles.some(r => hasRole(r));
    return roles.includes(user?.role);
  };

  const visibleItems = items.filter(i => can(i.roles));

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    logout();
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Solo cerrar si el click está fuera de AMBOS menús (desktop Y móvil)
      const clickedOutsideDesktop = userMenuRef.current && !userMenuRef.current.contains(event.target);
      const clickedOutsideMobile = mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(event.target);

      // Si hizo click fuera de ambos menús, cerrar
      if (clickedOutsideDesktop && clickedOutsideMobile) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      // Usar un pequeño delay para evitar que se cierre inmediatamente después de abrir
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [userMenuOpen]);

  // Manejar modal de contraseña
  const openPasswordModal = () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false); // También cerrar menú móvil
    setPasswordModalOpen(true);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  // Lock body scroll when password modal is open
  useEffect(() => {
    if (!passwordModalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [passwordModalOpen]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!newPassword || newPassword.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      await setUserPasswordApi(user.id, newPassword, token);
      setPasswordSuccess('Contraseña actualizada correctamente');
      setTimeout(() => {
        closePasswordModal();
      }, 2000);
    } catch (error) {
      setPasswordError(error?.message || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Build password modal via portal to avoid clipping by header/ancestors
  const passwordModal = passwordModalOpen ? createPortal(
    (
      <div
        role="dialog"
        aria-modal="true"
        className="password-modal-overlay"
        onClick={(e) => e.target === e.currentTarget && closePasswordModal()}
        onKeyDown={(e) => { if (e.key === 'Escape') closePasswordModal(); }}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '12px',
          overflowY: 'auto'
        }}
      >
        <div
          className="password-modal"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ffffff',
            color: '#111827',
            width: '100%',
            maxWidth: '520px',
            margin: 'auto',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            maxHeight: '90vh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            position: 'relative'
          }}
        >
          <div className="password-modal-header">
            <h3>Cambiar mi contraseña</h3>
            <button className="btn" onClick={closePasswordModal}>Cerrar</button>
          </div>

          <form onSubmit={handleChangePassword} className="password-modal-form">
            <div className="form-group">
              <label htmlFor="newPassword">Nueva contraseña</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                autoFocus
                disabled={loading}
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar contraseña</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                disabled={loading}
                style={{ width: '100%' }}
              />
            </div>

            {passwordError && (
              <div className="password-error">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="password-success">
                {passwordSuccess}
              </div>
            )}

            <div className="password-modal-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar contraseña'}
              </button>
            </div>
          </form>
        </div>
      </div>
    ),
    document.body
  ) : null;

  return (
    <header className={`navbar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
      {/* Desktop Navigation */}
      <nav className="nav-left">
        {visibleItems.map(i => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.to === '/'}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            title={i.label}
          >
            {i.label}
          </NavLink>
        ))}
      </nav>

      {/* Desktop User Info */}
      <div className="nav-right">
        {user && (
          <div className="user-menu-container" ref={userMenuRef}>
            <button
              className="user-name user-menu-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              title="Menú de usuario"
            >
              <FaUser style={{ marginRight: '0.5rem', fontSize: '0.75rem' }} />
              {user.name}
              <FaChevronDown style={{ marginLeft: '0.5rem', fontSize: '0.65rem' }} />
            </button>

            {userMenuOpen && (
              <div className="user-dropdown">
                <button
                  className="user-dropdown-item"
                  onClick={openPasswordModal}
                >
                  <FaKey style={{ marginRight: '0.5rem' }} />
                  Cambiar contraseña
                </button>
                <div className="user-dropdown-divider"></div>
                <button
                  className="user-dropdown-item logout-item"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt style={{ marginRight: '0.5rem' }} />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
      </button>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-nav">
          {visibleItems.map(i => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === '/'}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={closeMobileMenu}
            >
              {i.label}
            </NavLink>
          ))}
        </nav>

        {/* Mobile User Info */}
        <div className="mobile-user-info">
          {user && (
            <div className="user-menu-container-mobile" ref={mobileUserMenuRef}>
              <button
                className="user-name user-menu-trigger-mobile"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(!userMenuOpen);
                }}
                title="Menú de usuario"
                type="button"
              >
                <FaUser style={{ marginRight: '0.5rem', fontSize: '0.75rem' }} />
                {user.name}
                <FaChevronDown style={{ marginLeft: '0.5rem', fontSize: '0.65rem' }} />
              </button>

              {userMenuOpen && (
                <div className="user-dropdown-mobile">
                  <button
                    type="button"
                    className="user-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPasswordModal();
                    }}
                  >
                    <FaKey style={{ marginRight: '0.5rem' }} />
                    Cambiar contraseña
                  </button>
                  <div className="user-dropdown-divider"></div>
                  <button
                    type="button"
                    className="user-dropdown-item logout-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                  >
                    <FaSignOutAlt style={{ marginRight: '0.5rem' }} />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de cambio de contraseña (portal) */}
      {passwordModal}
    </header>
  );
}